import { Injectable } from '@angular/core';
import { AuthConfig, OAuthEvent, OAuthInfoEvent, OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';
import { ROOT_ROUTES } from '../configurations/router';
import { Store } from '@ngrx/store';
import { AUTH_ACTIONS } from '../../store/actions/auth.actions';
import { distinctUntilChanged, map, tap } from 'rxjs';

const authCodeFlowConfig: AuthConfig = {
  issuer: environment.idp_url,
  clientId: environment.client_id,
  redirectUri: `${window.location.origin}/${ROOT_ROUTES.signIn}`,
  responseType: 'code',
  useSilentRefresh: true,
  postLogoutRedirectUri: window.location.origin,
  silentRefreshRedirectUri: `${window.location.origin}/assets/silent-callback.html`,
  scope: 'openid profile conversations api-lhapi agentcall.manage',
  sessionCheckIntervall: 10000,
  timeoutFactor: 0.9,
  sessionChecksEnabled: true,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly oauthService: OAuthService,
    private readonly store: Store,
  ) {
    this.oauthService.configure(authCodeFlowConfig);
    this.initEventsListener();
    void this.initLoginCheck();
  }

  private get isAuth(): boolean {
    return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
  }

  logout(): void {
    void this.oauthService.revokeTokenAndLogout();
  }

  login(): void {
    void this.oauthService.initLoginFlow();
  }

  setAutomaticRefreshToken(): void {
    this.oauthService.setupAutomaticSilentRefresh();
  }

  setSessionChecksIfStillLoggedIn(): void {
    this.oauthService.restartSessionChecksIfStillLoggedIn();
  }

  async refreshToken(): Promise<void> {
    await this.oauthService.silentRefresh();
  }

  async initLoginCheck(): Promise<void> {
    this.store.dispatch(AUTH_ACTIONS.initLoginCheck());
    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.setAutomaticRefreshToken();
  }

  private async loadUserProfile(): Promise<void> {
    const { info: userProfile } = (await this.oauthService.loadUserProfile()) as { info: IUserAuthProfile };
    this.store.dispatch(AUTH_ACTIONS.userProfileLoaded({ userProfile }));
  }

  private initEventsListener(): void {
    this.oauthService.events
      .pipe(
        map(() => this.isAuth),
        distinctUntilChanged(),
        tap((authenticated) => this.authHandler(authenticated)),
      )
      .subscribe();

    this.oauthService.events.pipe(tap((event) => this.eventHandlers(event))).subscribe();
  }

  private authHandler(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      this.store.dispatch(AUTH_ACTIONS.authenticated());
    } else {
      this.store.dispatch(AUTH_ACTIONS.notAuthenticated());
    }
  }

  private eventHandlers(event: OAuthEvent): void {
    switch (event.type) {
      case 'discovery_document_loaded':
        this.onDiscoveryDocumentLoaded(event);
        break;
      case 'token_received':
        void this.loadUserProfile();
        break;
      case 'session_error':
      case 'session_terminated':
        this.onSessionTerminated();
        break;
      case 'invalid_nonce_in_state':
        this.login();
        break;
    }
  }

  private onDiscoveryDocumentLoaded(event: OAuthEvent): void {
    const hasInfo = Boolean((event as OAuthInfoEvent).info);
    if (hasInfo && this.isAuth) {
      void this.loadUserProfile();
    }
    this.store.dispatch(AUTH_ACTIONS.discoveryDocumentLoaded());
  }

  private onSessionTerminated(): void {
    this.oauthService.stopAutomaticRefresh();
    this.store.dispatch(AUTH_ACTIONS.sessionTerminated());
  }
}

export interface IUserAuthProfile {
  amr: string[];
  at_hash: string;
  aud: string;
  auth_time: number; // in seconds
  exp: number; // in seconds
  family_name: string;
  given_name: string;
  iat: number; // in seconds
  idp: string;
  iss: string;
  name: string;
  nbf: number; // in seconds
  nonce: string;
  preferred_username: string;
  s_hash: string;
  sid: string;
  sub: string;
  'user-id': number;
}
